import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * 予約 API 操作クラス
 */
export default class Reservations {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET Reservation
   */
  public async get(userStore: LoginUser, reservationId: number): Promise<ReservationsGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      reservationId: reservationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Reservation, formatParams);
    const response = await this.commonService.executeGet<ReservationsGetModel>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET Reservations
   */
  public async gets(userStore: LoginUser, queryParams: any): Promise<Array<ReservationsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Reservations, formatParams);
    const response = await this.commonService.executeGet<Array<ReservationsGetModel>>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * ### GET ReservationsCount
   */
  public async getCount(userStore: LoginUser, queryParams: any): Promise<number> {
    // countの場合は末尾に「count」をつける
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Reservations, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data.count;
  }

  /**
   * ### POST Reservation
   */
  public async post(userStore: LoginUser, residentId: number, requestBody: ReservationsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationResident, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH Reservation
   */
  public async patch(userStore: LoginUser, reservationId: number, requestBody: ReservationsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      reservationId: reservationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Reservation, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH Reservation
   */
  public async patchConsented(userStore: LoginUser, reservationId: number, requestBody: ReservationsConsentedPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      reservationId: reservationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationConsented, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE Reservation
   */
  public async delete(userStore: LoginUser, reservationId: number, requestBody: ReservationsDeleteModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      reservationId: reservationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationDelete, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### POST TransferOnlineReservationUrl
   */
  public async transferOnlineReservationUrl(userStore: LoginUser, reservationId: number, requestBody: TransferOnlineReservationUrlRequestBody): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      reservationId: reservationId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.TransferOnlineReservationUrl, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET ReservationsRemind
   */
  public async getRemind(userStore: LoginUser, year: string, month: string, date: string): Promise<Array<ReservationsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      year,
      month,
      date
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ReservationsRemind, formatParams);
    const response = await this.commonService.executeGet<Array<ReservationsGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * 面会予約GET Model
 */
export interface ReservationsGetModel {
  /** 施設を示すID */
  facilityId: number;
  /** 予約ID */
  id: number;
  /** 訪問日時(YYYYMMDDhhmmssSSS) */
  datetime: number | string;
  /** 連絡事項 */
  text?: string;
  /** 予約の種別 */
  type: ReservationType;
  /** 利用者を示すID */
  residentId: number;
  /** 入居者の氏名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 入居者の部屋名 */
  residentRoom: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族の氏名 */
  familyName: string;
  /** 家族の電話番号 */
  familyTel: string;
  /** 続柄 */
  familyRelation: string;
  /** 家族の役割 */
  familyPosition: string;
  /** 予約の同意 */
  consented: boolean;
  /** onlineReservationsSchedulesテーブルのソートキー */
  onlineReservationSchedulesSortKey?: string;
  /** reservationsSchedulesテーブルのソートキー */
  reservationSchedulesSortKey?: string;
  /** 予約の同意日 */
  consentedDate: number;
  /** 予約同意したユーザーID */
  consentedUserId: string;
  /** 予約同意したユーザー名 */
  consentedUserName: string;
  /** 同一利用者の家族に共有するか否か。v3.38.0以前はundefined */
  isSharedReservation: boolean;
  /** 面会場所 */
  location?: string;
  /** オンライン面会URL */
  onlineVisitUrl?: string;
  /** 入力種別 */
  inputType: InputType;
  /** グループID */
  groupId: number;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 訪問日時登録日(YYYYMMDDhhmmssSSS) */
  created: number | string;
  /** 訪問日時の修正日(YYYYMMDDhhmmssSSS) */
  updated: number | string;
}

/**
 * ### 予約種別
 */
export enum ReservationType {
  /** 訪問 */
  VISIT = 0,
  /** オンライン */
  ONLINE = 1
}

export interface ReservationsPostModel {
  groupId: number;
  familyId: number;
  datetime: number;
  /** 連絡事項 */
  text?: string;
  familyName: string;
  residentName: string;
  residentRoom: string;
  familyTel: string;
  familyRelation: string;
  familyPosition: string;
  familyLineId?: string;
  familyMailaddress?: string;
  consented: boolean;
  /** 面会場所 */
  location: string;
  /** 同一利用者の家族に共有するか否か。v3.38.0以前はundefined */
  isSharedReservation: boolean;
  userId: string;
  userName: string;
  inputType: InputType;
}

export enum InputType {
  FACILITY_APP = 0,
  BOT_APP = 1
}

export interface ReservationsPatchModel {
  /** 連絡事項 */
  text?: string;
  /** 面会場所 */
  location?: string;
  /** 整合性チェック用の更新日付 */
  updated: number;
}

/**
 * ### 面会予約リクエストボディ
 */
export interface ReservationsConsentedPatchModel {
  /** 予約同意可否 */
  consented: boolean;
  /** 同意したユーザーID */
  consentedUserId: string;
  /** 同意したユーザー名 */
  consentedUserName: string;
  /** 整合性チェック用の更新日付 */
  updated: number;
}

export interface ReservationsDeleteModel {
  /** メッセージタイトル */
  title: string;
  /** メッセージ本文 */
  text: string;
  /** 送信種別 */
  sendType: ReservationDeleteSendType;
  /** 予約の種別 */
  reservationType: ReservationType;
}

/**
 * ### 面会予約オンラインURL転送リクエストボディ
 */
export interface TransferOnlineReservationUrlRequestBody {
  /** 送信対象のLINE ID */
  otherUserGroupsSortKeys: Array<string>;
}

/** 面会予約削除の種別 */
export enum ReservationDeleteSendType {
  /** 送信しない */
  NONE = "none",
  /** LINE */
  LINE = "line",
  /** メール */
  MAIL = "mail"
}
